import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Globe, Key, Shield, User, Eye, Trash2, Copy, Clock, AlertTriangle, Check, FlaskConical, Calendar, Pill, Search, ChevronDown } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { NEU } from '../neuStyles';
import { matchesNameQuery } from '../lib/nameSearch';

export default function PatientPortalPage({ user }) {
  const [patients, setPatients]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const dropdownRef = useRef(null);

  // Generate Access state
  const [selectedPatient, setSelectedPatient] = useState('');
  const [validityPeriod, setValidityPeriod]   = useState('30');
  const [generatedAccess, setGeneratedAccess] = useState(null);
  const [copiedField, setCopiedField]         = useState(null);

  // Portal tokens state — list of active portal tokens from portal:get-status
  const [portalTokens, setPortalTokens] = useState([]);

  // Portal preview state
  const [previewTokenId, setPreviewTokenId] = useState(null);
  const [portalData, setPortalData]         = useState(null);

  useEffect(() => {
    fetchPatients();
    fetchPortalTokens();
  }, []);

  const fetchPatients = async () => {
    try {
      const result = await window.zignagenixAPI.getPatients();
      console.log('[PatientPortal] getPatients result:', typeof result, Array.isArray(result), Array.isArray(result) ? result.length : 'N/A', result?.error || 'no error');
      if (Array.isArray(result) && result.length > 0) {
        console.log('[PatientPortal] First patient keys:', Object.keys(result[0]));
      }
      // Handle both plain array and {rows: [...]} wrapper
      const list = Array.isArray(result) ? result : (Array.isArray(result?.rows) ? result.rows : []);
      setPatients(list);
    } catch (err) {
      console.error('Error fetching patients:', err);
    }
  };

  const fetchPortalTokens = async () => {
    try {
      const result = await window.zignagenixAPI.portalGetStatus();
      // portal:get-status with no arg returns an array of active tokens
      setPortalTokens(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Error fetching portal tokens:', err);
      setPortalTokens([]);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sort patients alphabetically by last_name, first_name and group by letter
  const sortedGroupedPatients = useMemo(() => {
    const sorted = [...patients].sort((a, b) => {
      const lastA = (a.last_name || '').toLowerCase();
      const lastB = (b.last_name || '').toLowerCase();
      if (lastA !== lastB) return lastA.localeCompare(lastB);
      return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
    });

    // Filter by search term
    const term = patientSearch.toLowerCase().trim();
    const filtered = term
      ? sorted.filter(p => matchesNameQuery([p.last_name, p.first_name, p.mrn], term))
      : sorted;

    // Group by first letter of last name
    const groups = {};
    for (const p of filtered) {
      const letter = (p.last_name || p.first_name || '#')[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(p);
    }
    return { sorted: filtered, groups, letters: Object.keys(groups).sort() };
  }, [patients, patientSearch]);

  // Get selected patient display name
  const selectedPatientDisplay = useMemo(() => {
    const p = patients.find(pt => String(pt.id) === String(selectedPatient));
    if (!p) return '';
    return `${p.last_name || ''}, ${p.first_name || ''} — MRN: ${p.mrn || p.id}`;
  }, [patients, selectedPatient]);

  const handleGenerateAccess = async () => {
    if (!selectedPatient) { alert('Please select a patient.'); return; }
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.portalCreateAccess(selectedPatient, parseInt(validityPeriod));
      if (result?.error) { alert(result.message || 'Failed to generate portal access'); return; }
      setGeneratedAccess(result);
      await fetchPortalTokens();
    } catch (err) {
      console.error('Error generating access:', err);
      alert('Failed to generate portal access');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAccess = async (patientId) => {
    if (!window.confirm("Are you sure you want to revoke this patient's portal access?")) return;
    setLoading(true);
    try {
      await window.zignagenixAPI.portalRevokeAccess(patientId);
      await fetchPortalTokens();
      if (previewTokenId) { setPreviewTokenId(null); setPortalData(null); }
    } catch (err) {
      console.error('Error revoking access:', err);
      alert('Failed to revoke portal access');
    } finally {
      setLoading(false);
    }
  };

  const handleShowPortalPreview = async (tokenId) => {
    setLoading(true);
    try {
      const data = await window.zignagenixAPI.portalGetData(tokenId);
      if (data?.error) { alert(data.message || 'Failed to load portal preview'); return; }
      setPortalData(data);
      setPreviewTokenId(tokenId);
    } catch (err) {
      console.error('Error fetching portal data:', err);
      alert('Failed to load portal preview');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  if (!user?.permissions?.includes('portal:manage')) {
    return (
      <div className="page">
        <h1 className="page-title">Patient Portal Management</h1>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6C757D' }}>
          <Shield size={48} style={{ marginBottom: '1rem', opacity: 0.4 }} />
          <p>You do not have permission to manage patient portal access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Patient Portal Management</h1>
        <PrintButton pageTitle="Patient Portal Management" />
      </div>

      {/* ── Generate Portal Access ─────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
          <Key size={20} color="#2563eb" />
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>Generate Portal Access</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0, position: 'relative' }} ref={dropdownRef}>
            <label className="form-label">Select Patient</label>
            {/* Custom searchable alphabetical dropdown */}
            <div
              className="form-input"
              style={{
                cursor: generatedAccess ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                opacity: generatedAccess ? 0.6 : 1,
                minHeight: '38px', userSelect: 'none',
              }}
              onClick={() => !generatedAccess && setDropdownOpen(!dropdownOpen)}
            >
              <span style={{ color: selectedPatient ? '#111827' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedPatientDisplay || '— Choose a patient —'}
              </span>
              <ChevronDown size={16} color="#6b7280" style={{ flexShrink: 0, transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {dropdownOpen && !generatedAccess && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: '#fff', borderRadius: '0.5rem', marginTop: '4px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.08)',
                border: '1px solid #e5e7eb', maxHeight: '360px', display: 'flex', flexDirection: 'column',
              }}>
                {/* Search bar */}
                <div style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Search size={14} color="#9ca3af" />
                  <input
                    type="text"
                    placeholder="Search by name or MRN..."
                    value={patientSearch}
                    onChange={e => setPatientSearch(e.target.value)}
                    autoFocus
                    style={{
                      border: 'none', outline: 'none', flex: 1, fontSize: '0.875rem',
                      background: 'transparent', color: '#111827',
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                  {patientSearch && (
                    <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{sortedGroupedPatients.sorted.length} results</span>
                  )}
                </div>

                {/* Alphabetical letter quick-jump bar */}
                {sortedGroupedPatients.letters.length > 3 && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '0.35rem 0.5rem',
                    borderBottom: '1px solid #f3f4f6', background: '#f9fafb',
                  }}>
                    {sortedGroupedPatients.letters.map(letter => (
                      <button
                        key={letter}
                        onClick={(e) => {
                          e.stopPropagation();
                          const el = document.getElementById(`portal-patient-group-${letter}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        style={{
                          background: 'none', border: '1px solid #e5e7eb', borderRadius: '3px',
                          padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                          color: '#2563eb', lineHeight: 1.4,
                        }}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>
                )}

                {/* Scrollable grouped patient list */}
                <div style={{ overflowY: 'auto', maxHeight: '270px' }}>
                  {sortedGroupedPatients.sorted.length === 0 ? (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                      {patients.length === 0 ? 'No patients found' : 'No matches for search'}
                    </div>
                  ) : (
                    sortedGroupedPatients.letters.map(letter => (
                      <div key={letter} id={`portal-patient-group-${letter}`}>
                        {/* Letter header */}
                        <div style={{
                          padding: '0.25rem 0.75rem', background: '#f3f4f6',
                          fontSize: '0.7rem', fontWeight: 700, color: '#6b7280',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                          position: 'sticky', top: 0, zIndex: 1,
                        }}>
                          {letter}
                        </div>
                        {/* Patients in this letter group */}
                        {sortedGroupedPatients.groups[letter].map(p => (
                          <div
                            key={p.id}
                            onClick={() => {
                              setSelectedPatient(String(p.id));
                              setDropdownOpen(false);
                              setPatientSearch('');
                            }}
                            style={{
                              padding: '0.5rem 0.75rem', cursor: 'pointer',
                              fontSize: '0.85rem', color: '#111827',
                              background: String(p.id) === String(selectedPatient) ? '#eff6ff' : 'transparent',
                              borderLeft: String(p.id) === String(selectedPatient) ? '3px solid #2563eb' : '3px solid transparent',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                            onMouseEnter={e => { if (String(p.id) !== String(selectedPatient)) e.currentTarget.style.background = '#f9fafb'; }}
                            onMouseLeave={e => { if (String(p.id) !== String(selectedPatient)) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span>
                              <strong>{p.last_name || ''}</strong>{p.last_name && p.first_name ? ', ' : ''}{p.first_name || ''}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>MRN: {p.mrn || p.id}</span>
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Validity Period</label>
            <select
              className="form-input"
              value={validityPeriod}
              onChange={e => setValidityPeriod(e.target.value)}
              disabled={!!generatedAccess}
            >
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
            </select>
          </div>

          <button
            className="btn-primary"
            onClick={handleGenerateAccess}
            disabled={loading || !!generatedAccess}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
          >
            <Globe size={16} /> Generate Access
          </button>
        </div>

        {generatedAccess && (
          <div style={{ marginTop: '1.25rem', ...NEU.card, background: '#f0fdf4', border: 'none', padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', ...NEU.inset, background: '#fef3c7', borderLeft: '4px solid #F26522', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#92400e' }}>
              <AlertTriangle size={18} />
              <span style={{ fontWeight: 600 }}>One-time display — save these credentials now. They cannot be retrieved again.</span>
            </div>

            {[
              { label: 'Access Code', value: generatedAccess.accessCode, field: 'code' },
              { label: 'PIN', value: generatedAccess.pin, field: 'pin' },
            ].map(({ label, value, field }) => (
              <div key={field} style={{ marginBottom: '0.875rem' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{label}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', ...NEU.input, padding: '0.75rem' }}>
                  <code style={{ flex: 1, fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: '#111827', letterSpacing: '0.1em' }}>{value}</code>
                  <button className="btn-sm btn-ghost" onClick={() => copyToClipboard(value, field)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {copiedField === field ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', marginBottom: '1rem' }}>
              <Clock size={14} />
              <span>Expires: <strong>{formatDate(generatedAccess.expiresAt)}</strong></span>
            </div>

            <button className="btn-ghost btn-sm" onClick={() => { setGeneratedAccess(null); setSelectedPatient(''); }}>
              + Generate Another
            </button>
          </div>
        )}
      </div>

      {/* ── Active Portal Tokens ──────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
          <User size={20} color="#4FA038" />
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
            Active Portal Access <span style={{ fontWeight: 400, fontSize: '0.875rem', color: '#6C757D' }}>({portalTokens.length})</span>
          </h2>
        </div>

        {portalTokens.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
            <Globe size={36} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No active portal access tokens</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {portalTokens.map(token => {
              // DB returns snake_case: first_name, last_name, is_active, last_access, expires_at, patient_id
              const patientName = token.first_name
                ? `${token.first_name} ${token.last_name}`
                : token.patientName || `Patient #${token.patient_id}`;
              const isExpired = token.expires_at && new Date(token.expires_at) < new Date();

              return (
                <div key={token.id} style={{ ...NEU.cardSm, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: '#f9fafb' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: '#111827', marginBottom: '0.375rem' }}>
                      <User size={15} />
                      {patientName}
                      {token.mrn && <span style={{ fontSize: '0.75rem', color: '#6C757D', fontWeight: 400 }}>MRN: {token.mrn}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.8rem' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '999px', fontWeight: 600, fontSize: '0.7rem',
                        background: isExpired ? '#fee2e2' : '#dcfce7',
                        color: isExpired ? '#991b1b' : '#166534',
                      }}>
                        {isExpired ? 'Expired' : 'Active'}
                      </span>
                      <span style={{ color: '#6C757D' }}>
                        Expires: {formatDate(token.expires_at || token.expiresAt)}
                      </span>
                      <span style={{ color: '#6C757D' }}>
                        {(token.last_access || token.lastAccess)
                          ? `Last accessed: ${formatDate(token.last_access || token.lastAccess)}`
                          : 'Never accessed'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    <button
                      className="btn-sm btn-ghost"
                      onClick={() => handleShowPortalPreview(token.id)}
                      disabled={loading}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Eye size={14} /> Preview
                    </button>
                    <button
                      className="btn-sm"
                      onClick={() => handleRevokeAccess(token.patient_id || token.patientId)}
                      disabled={loading}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#991b1b', border: 'none', boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}
                    >
                      <Trash2 size={14} /> Revoke
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Portal Preview ────────────────────────────────────────── */}
      {previewTokenId && portalData && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
            <Eye size={20} color="#2E8B98" />
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>Portal Preview</h2>
            <button
              className="btn-ghost btn-sm"
              onClick={() => { setPreviewTokenId(null); setPortalData(null); }}
              style={{ marginLeft: 'auto' }}
            >
              Close Preview
            </button>
          </div>

          <div style={{ ...NEU.inset, background: '#dbeafe', borderLeft: '4px solid #2E8B98', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1e40af', marginBottom: '1.25rem', fontSize: '0.875rem' }}>
            <Shield size={15} /> Read-only staff preview of what the patient sees in their portal
          </div>

          <div style={{ ...NEU.inset, padding: '1.25rem' }}>
            {/* Personal Info */}
            {portalData.patient && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Personal Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
                  {[
                    ['Name', `${portalData.patient.first_name || ''} ${portalData.patient.last_name || ''}`.trim()],
                    ['Date of Birth', formatDate(portalData.patient.date_of_birth)],
                    ['Phone', portalData.patient.phone || '—'],
                    ['Email', portalData.patient.email || '—'],
                  ].map(([label, value]) => (
                    <div key={label} style={{ ...NEU.input, padding: '0.75rem' }}>
                      <span style={{ display: 'block', fontSize: '0.7rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</span>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Appointments */}
            {portalData.appointments?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Calendar size={14} /> Upcoming Appointments
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {portalData.appointments.map((apt, i) => (
                    <div key={i} style={{ ...NEU.cardSm, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#F8F9FA', borderLeft: '3px solid #62BB46' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{(apt.appointment_type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#6C757D' }}>with {apt.provider_name || 'Provider'}</span>
                      </div>
                      <span style={{ fontSize: '0.8rem', color: '#374151' }}>{formatDate(apt.appointment_date)} {formatTime(apt.start_time)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medications */}
            {portalData.medications?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Pill size={14} /> Medications
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {portalData.medications.map((med, i) => (
                    <div key={i} style={{ ...NEU.cardSm, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#F8F9FA', borderLeft: '3px solid #2E8B98' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{med.drug_name}</span>
                      <span style={{ fontSize: '0.8rem', color: '#6C757D' }}>{med.dosage} · {med.frequency}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lab Results */}
            {portalData.labs?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <FlaskConical size={14} /> Recent Lab Results
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {portalData.labs.map((lab, i) => (
                    <div key={i} style={{ ...NEU.cardSm, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: lab.is_abnormal ? '#fff7ed' : '#F8F9FA', borderLeft: `3px solid ${lab.is_abnormal ? '#DC3545' : '#2E8B98'}` }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{lab.test_name}</span>
                      <span style={{ fontSize: '0.8rem', color: lab.is_abnormal ? '#DC3545' : '#374151', fontWeight: lab.is_abnormal ? 700 : 400 }}>
                        {lab.result_value} {lab.result_unit}
                        {lab.is_abnormal && <span style={{ marginLeft: '6px', fontSize: '0.7rem', background: '#fee2e2', color: '#DC3545', padding: '1px 5px', borderRadius: '4px' }}>Abnormal</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Allergies */}
            {portalData.allergies?.length > 0 && (
              <div>
                <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <AlertTriangle size={14} /> Allergies
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {portalData.allergies.map((a, i) => (
                    <span key={i} style={{ padding: '4px 12px', background: '#fff7ed', color: '#9a3412', border: 'none', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>
                      {a.allergen}{a.severity ? ` (${a.severity})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
